import './CategoryTabs.scss';

import { Icon, Tabs } from '@modulor/react';
import { useSkeletonTranslation } from 'hooks/useSkeletonTranslation';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { getClickStreamEvent, getEventAnalytic, useGetClickStreamCashierContext } from 'services/clickstream';
import { useAppSelector } from 'store/hooks';
import { paymentFlowSelector } from 'store/slices/global';
import { isShowGroupingMethodsInCarouselSelector } from 'store/slices/methodGrouping';
import { getLayoutType, LayoutType } from 'utils';
import { useGetSessionId } from 'utils/session';

export type TabsData = {
  name?: string;
  disabled?: boolean;
  badge?: {
    type: 'counter-primary' | 'counter-secondary' | 'text' | 'error' | 'dot';
    value?: string | number;
  };
};

interface CategoryTabsProps {
  list: TabsData[];
  onSelect?: (categoryName: string) => void;
  selectedTab?: string;
}

const TABS_SELECTOR = '[data-testid="modulor-tabs"]';
const SCROLL_AMOUNT = 200;

const clickstream = getEventAnalytic();

const CategoryTabs: React.FC<CategoryTabsProps> = ({ list, onSelect, selectedTab }) => {
  const { translate } = useSkeletonTranslation();
  const flowType = useAppSelector(paymentFlowSelector);
  const isShowGroupingMethodsInCarousel = useAppSelector(isShowGroupingMethodsInCarouselSelector);
  const cashierContext = useGetClickStreamCashierContext(flowType);
  const sessionId = useGetSessionId();
  const tabsContainerRef = useRef<HTMLDivElement>(null);
  const isDesktopLayout = getLayoutType() === LayoutType.desktop;
  const prevSelectedTabRef = useRef<string | undefined>(selectedTab);
  const initialIndex = useMemo(() => {
    if (!selectedTab) return 0;
    const index = list.findIndex((tab) => tab.name === selectedTab);
    return index !== -1 ? index : 0;
  }, [selectedTab, list]);
  const translatedTabs = useMemo(
    () =>
      list.map((tab) => ({
        ...tab,
        name: tab.name ? translate(tab.name) : tab.name,
        badge: tab.badge
          ? {
              ...tab.badge,
              value: typeof tab.badge.value === 'string' ? translate(tab.badge.value) : tab.badge.value,
            }
          : undefined,
      })),
    [list, translate],
  );
  const [selectedTabIndex, setSelectedTabIndex] = useState(initialIndex);
  const [showLeftArrow, setShowLeftArrow] = useState(false);
  const [showRightArrow, setShowRightArrow] = useState(false);

  const getTabsContainer = () => tabsContainerRef.current?.querySelector(TABS_SELECTOR) as HTMLElement | null;

  const checkScroll = () => {
    const tabsContainer = getTabsContainer();
    if (!tabsContainer) return;

    const { scrollLeft, scrollWidth, clientWidth } = tabsContainer;
    setShowLeftArrow(scrollLeft > 0);
    setShowRightArrow(scrollLeft < scrollWidth - clientWidth - 1);
  };

  const scrollTabs = (direction: 'left' | 'right') => {
    const tabsContainer = getTabsContainer();
    if (!tabsContainer) return;

    const newScrollLeft =
      direction === 'left' ? tabsContainer.scrollLeft - SCROLL_AMOUNT : tabsContainer.scrollLeft + SCROLL_AMOUNT;

    tabsContainer.scrollTo({
      left: newScrollLeft,
      behavior: 'smooth',
    });
  };

  const handleTabChange = useCallback(
    (newIndex: number) => {
      if (onSelect && list[newIndex]?.name) {
        onSelect(list[newIndex].name as string);
        const event = getClickStreamEvent.pw_category_click(sessionId);
        clickstream.push(event, [cashierContext]);
      }
    },
    [list, onSelect],
  );

  // Synchronization with external selectedTab (from Intersection Observer)
  useEffect(() => {
    if (selectedTab && selectedTab !== prevSelectedTabRef.current) {
      const index = list.findIndex((tab) => tab.name === selectedTab);
      if (index !== -1) {
        setSelectedTabIndex(index);
      }
      prevSelectedTabRef.current = selectedTab;
    }
  }, [selectedTab, list]);

  // Automatically scroll to the selected tab
  useEffect(() => {
    const tabsContainer = getTabsContainer();
    if (!tabsContainer) return;

    const activeTab = tabsContainer.children[selectedTabIndex] as HTMLElement;
    if (activeTab) {
      const containerRect = tabsContainer.getBoundingClientRect();
      const activeRect = activeTab.getBoundingClientRect();
      const scrollLeft = activeTab.offsetLeft - containerRect.width / 2 + activeRect.width / 2;

      tabsContainer.scrollTo({
        left: scrollLeft,
        behavior: 'smooth',
      });
    }
  }, [selectedTabIndex]);

  // Initialization and scroll handling
  useEffect(() => {
    const tabsContainer = getTabsContainer();
    if (!tabsContainer) return undefined;

    checkScroll();
    const timeoutId = setTimeout(checkScroll, 300);

    tabsContainer.addEventListener('scroll', checkScroll);
    window.addEventListener('resize', checkScroll);

    return () => {
      clearTimeout(timeoutId);
      tabsContainer.removeEventListener('scroll', checkScroll);
      window.removeEventListener('resize', checkScroll);
    };
  }, [checkScroll]);

  useEffect(() => {
    checkScroll();
  }, [list]);

  if (!list || list.length === 0) {
    return null;
  }

  return (
    <div className={`carousel-with-tabs${isShowGroupingMethodsInCarousel ? ' carousel-with-tabs--custom-margin' : ''}`}>
      {isDesktopLayout && showLeftArrow && (
        <div className="arrow-button left" onClick={() => scrollTabs('left')}>
          <Icon name="chevron_left" />
        </div>
      )}

      <div ref={tabsContainerRef} className="tabs-container">
        <Tabs selectedIndex={selectedTabIndex} setSelectedIndex={handleTabChange} tabs={translatedTabs} />
      </div>

      {isDesktopLayout && showRightArrow && (
        <div className="arrow-button right" onClick={() => scrollTabs('right')}>
          <Icon name="chevron_right" />
        </div>
      )}
    </div>
  );
};

export default CategoryTabs;
